export type HistorySharingPermissions = {
  shareJobHistory?: boolean;
  shareDeploymentHistory?: boolean;
};

export type AssetHistoryActivityNode = {
  kind?: string | null;
  createdAt: string;
  createdBy?: {
    id: string;
    name: string;
    image?: string | null;
  } | null;
  job?: {
    id: string;
    name: string;
    status: string;
    completedAt?: string | null;
    assigneeNotes?: string | null;
    site?: { name?: string | null } | null;
  } | null;
  task?: {
    id?: string | null;
    name: string;
    notes?: string | null;
    attachments?: Array<{
      id: string;
      kind: string;
      thumb: string;
      url: string;
      originalFilename: string;
    }> | null;
  } | null;
  comment?: {
    id: string;
    body?: string | null;
    attachments?: Array<{
      id: string;
      kind: string;
      thumb: string;
      url: string;
      originalFilename: string;
    }> | null;
  } | null;
};

export type DeploymentItem = {
  id: string;
  status: string;
  startedAt: string;
  image?: string | null;
  displayLocation?: string | null;
};

export type CompletedJobGroup = {
  jobId: string;
  jobName: string;
  siteName?: string | null;
  completedAt: string;
  assigneeNotes?: string | null;
  completedBy?: {
    id: string;
    name: string;
    image?: string | null;
  } | null;
  taskNotes: Array<{
    taskId?: string | null;
    taskName: string;
    notes?: string | null;
    attachments?: Array<{
      id: string;
      kind: string;
      thumb: string;
      url: string;
      originalFilename: string;
    }> | null;
  }>;
  messages: Array<{
    id: string;
    body?: string | null;
    createdBy?: {
      id: string;
      name: string;
      image?: string | null;
    } | null;
    attachments?: Array<{
      id: string;
      kind: string;
      thumb: string;
      url: string;
      originalFilename: string;
    }> | null;
  }>;
};

export type HistoryItem =
  | { type: 'completedJob'; data: CompletedJobGroup; timestamp: string }
  | { type: 'deployment'; data: DeploymentItem; timestamp: string };

const COMPLETED_JOB_STATUS = 'Complete';

/**
 * Group completed-job activity with deployments, dedupe notes/messages,
 * and sort newest-first. Incomplete jobs are excluded.
 */
export function buildAssetHistoryItems(
  activityEdges: Array<{ node: AssetHistoryActivityNode }>,
  deployments: DeploymentItem[]
): HistoryItem[] {
  const items: HistoryItem[] = [];
  const jobGroups = new Map<string, CompletedJobGroup>();

  for (const edge of activityEdges) {
    const node = edge.node;
    const job = node.job;

    // Skip if no job or job is not completed
    if (!job || job.status !== COMPLETED_JOB_STATUS) {
      continue;
    }

    const jobId = job.id;

    if (!jobGroups.has(jobId)) {
      jobGroups.set(jobId, {
        jobId,
        jobName: job.name,
        siteName: job.site?.name,
        completedAt: job.completedAt || node.createdAt,
        assigneeNotes: job.assigneeNotes || null,
        completedBy: node.createdBy,
        taskNotes: [],
        messages: [],
      });
    }

    const group = jobGroups.get(jobId)!;

    // Assignee notes live in their own section; prefer comment.body over job.assigneeNotes
    if (node.kind === 'assigneeNotes') {
      if (node.comment?.body) {
        group.assigneeNotes = node.comment.body;
      }
      continue;
    }

    if (node.task && (node.task.notes || node.task.attachments?.length)) {
      const taskId = node.task.id;
      const taskName = node.task.name;
      const existingTask = group.taskNotes.find((t) => {
        if (taskId && t.taskId) {
          return t.taskId === taskId;
        }
        return t.taskName === taskName;
      });
      if (!existingTask) {
        group.taskNotes.push({
          taskId: taskId,
          taskName: taskName,
          notes: node.task.notes,
          attachments: node.task.attachments,
        });
      }
    }

    if (
      node.comment &&
      (node.comment.body || node.comment.attachments?.length)
    ) {
      const existingMessage = group.messages.find(
        (m) => m.id === node.comment?.id
      );
      if (!existingMessage) {
        group.messages.push({
          id: node.comment.id,
          body: node.comment.body,
          createdBy: node.createdBy,
          attachments: node.comment.attachments,
        });
      }
    }
  }

  jobGroups.forEach((group) => {
    items.push({
      type: 'completedJob',
      data: group,
      timestamp: group.completedAt,
    });
  });

  for (const deployment of deployments) {
    items.push({
      type: 'deployment',
      data: deployment,
      timestamp: deployment.startedAt,
    });
  }

  items.sort((a, b) => {
    const dateA = new Date(a.timestamp).getTime();
    const dateB = new Date(b.timestamp).getTime();
    return dateB - dateA;
  });

  return items;
}

/**
 * External users only see history the task sharing flags allow.
 * Internal viewers (no permissions object) see everything.
 *
 * Asymmetry: job history is shown unless explicitly false;
 * deployment history is hidden unless explicitly true.
 */
export function filterHistoryBySharingPermissions(
  items: HistoryItem[],
  sharingPermissions?: HistorySharingPermissions
): HistoryItem[] {
  if (sharingPermissions === undefined) {
    return items;
  }

  return items.filter((item) => {
    if (item.type === 'completedJob') {
      return sharingPermissions.shareJobHistory !== false;
    }
    if (item.type === 'deployment') {
      return sharingPermissions.shareDeploymentHistory === true;
    }
    return true;
  });
}
